import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../utils/useMediaQuery';
import useDialog from '../utils/useDialog';

/**
 * Quest definitions.
 * Each quest has a `check(discoveries)` function that returns true when complete.
 */
export const QUESTS = [
  {
    id:      'first_animal',
    icon:    '🐾',
    title:   'Meet a wild animal',
    desc:    'Open the Animals card in any country',
    xp:      10,
    check:   (d) => d.some(x => x.type === 'animal'),
  },
  {
    id:      'first_food',
    icon:    '🍽️',
    title:   'Taste the world',
    desc:    'Open the Local food card in any country',
    xp:      10,
    check:   (d) => d.some(x => x.type === 'food'),
  },
  {
    id:      'first_landmark',
    icon:    '🏛️',
    title:   'Visit a famous place',
    desc:    'Open the Famous place card in any country',
    xp:      10,
    check:   (d) => d.some(x => x.type === 'landmark'),
  },
  {
    id:      'first_fact',
    icon:    '✨',
    title:   'Blow your mind',
    desc:    'Discover a fun fact in any country',
    xp:      10,
    check:   (d) => d.some(x => x.type === 'fact'),
  },
  {
    id:      'three_countries',
    icon:    '🌍',
    title:   'World traveller',
    desc:    'Explore 3 different countries',
    xp:      25,
    check:   (d) => new Set(d.map(x => x.country)).size >= 3,
  },
  {
    id:      'five_stickers',
    icon:    '⭐',
    title:   'Sticker collector',
    desc:    'Collect 5 discovery stickers',
    xp:      20,
    check:   (d) => d.length >= 5,
  },
  {
    id:      'full_country',
    icon:    '🏆',
    title:   'Country expert',
    desc:    'Open all 5 cards in one country',
    xp:      30,
    check:   (d) => {
      const byCountry = {};
      d.forEach(x => {
        if (!byCountry[x.country]) byCountry[x.country] = new Set();
        byCountry[x.country].add(x.type);
      });
      return Object.values(byCountry).some(types => types.size >= 5);
    },
  },
];

/**
 * QuestList — shows auto-tracked quests
 *
 * Props:
 *   discoveries — [{ country, type, ts }]
 *   onClose     — () => void
 */
export default function QuestList({ discoveries, onClose }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const dialogRef = useDialog({ onClose, trapFocus: false });
  const completed = QUESTS.filter(q => q.check(discoveries));
  const pending   = QUESTS.filter(q => !q.check(discoveries));
  const totalXp   = completed.reduce((sum, q) => sum + q.xp, 0);
  const reduced = useReducedMotion();

  return (
    <motion.div
      className="orboot-scroll"
      style={{ ...s.panel, ...(isMobile ? s.panelMobile : {}) }}
      ref={dialogRef}
      tabIndex={-1}
      role="region"
      aria-label="My quests"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    >

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ ...s.header, ...(isMobile ? s.headerMobile : {}) }}>
        <button style={s.closeBtn} onClick={onClose} aria-label="Back to globe">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div style={s.titleBlock}>
          <h1 style={{ ...s.title, ...(isMobile ? s.titleMobile : {}) }}>Quests</h1>
          <p style={s.subtitle}>{completed.length} of {QUESTS.length} complete · {totalXp} XP</p>
        </div>
        <span style={{ ...s.mapEmoji, ...(isMobile ? s.mapEmojiMobile : {}) }} aria-hidden="true">🗺️</span>
      </div>

      {/* ── XP bar ──────────────────────────────────────────────── */}
      <div style={{ ...s.xpWrap, ...(isMobile ? s.xpWrapMobile : {}) }} aria-label={`${totalXp} XP earned`}>
        <div style={s.xpTrack}>
          <div
            style={{
              ...s.xpFill,
              width: `${Math.min(100, (totalXp / 115) * 100)}%`,
            }}
          />
        </div>
        <span style={s.xpLabel}>{totalXp} XP</span>
      </div>

      {/* ── Active quests ────────────────────────────────────────── */}
      {pending.length > 0 && (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>Up next</h2>
          {pending.map(q => (
          <QuestCard key={q.id} quest={q} done={false} isMobile={isMobile} />
          ))}
        </section>
      )}

      {/* ── Completed ───────────────────────────────────────────── */}
      {completed.length > 0 && (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>Completed</h2>
          {completed.map(q => (
          <QuestCard key={q.id} quest={q} done={true} isMobile={isMobile} />
          ))}
        </section>
      )}

      {/* ── Empty state ─────────────────────────────────────────── */}
      {discoveries.length === 0 && (
        <div style={s.emptyWrap}>
          <div style={s.emptyEmoji}>🌏</div>
          <p style={s.emptyText}>Tap a country to start your first quest!</p>
        </div>
      )}

    </motion.div>
  );
}

function QuestCard({ quest, done, isMobile }) {
  return (
    <div style={{ ...qs.card, ...(isMobile ? qs.cardMobile : {}), ...(done ? qs.cardDone : {}) }}>
      <span style={qs.icon} aria-hidden="true">{quest.icon}</span>
      <div style={qs.body}>
        <p style={{ ...qs.title, ...(done ? qs.titleDone : {}) }}>{quest.title}</p>
        <p style={qs.desc}>{quest.desc}</p>
      </div>
      <div style={qs.right}>
        {done
          ? (
            <span style={qs.checkWrap} aria-label="Completed">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12l5 5 9-11" />
              </svg>
            </span>
          )
          : <span style={qs.xp}>+{quest.xp} XP</span>
        }
      </div>
    </div>
  );
}

const s = {
  panel: {
    position:      'absolute',
    inset:         0,
    background:    '#ffffff',
    color:         '#0f172a',
    overflowY:     'auto',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)',
  },
  panelMobile: {
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
  },
  header: {
    display:      'flex',
    alignItems:   'center',
    gap:          14,
    padding:      '24px 24px 18px',
    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
    position:     'sticky',
    top:          0,
    background:   'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    zIndex:       2,
  },
  headerMobile: {
    padding: '16px 16px 14px',
    gap: 12,
  },
  closeBtn: {
    width: 40, height: 40,
    borderRadius: '50%',
    border:       '1px solid rgba(15, 23, 42, 0.08)',
    background:   '#ffffff',
    fontSize:     16,
    fontWeight:   600,
    color:        '#0f172a',
    boxShadow:    '0 2px 8px rgba(15, 23, 42, 0.06)',
    flexShrink:   0,
    cursor:       'pointer',
    display:      'flex', alignItems: 'center', justifyContent: 'center',
  },
  titleBlock: { flex: 1 },
  title: {
    fontSize:   24,
    fontWeight: 700,
    color:      '#0f172a',
    margin:     0,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  titleMobile: {
    fontSize: 20,
  },
  subtitle: {
    fontSize:   13.5,
    color:      '#64748b',
    fontWeight: 500,
    margin:     '4px 0 0',
    letterSpacing: '-0.005em',
  },
  mapEmoji: { fontSize: 28, opacity: 0.85 },
  mapEmojiMobile: { fontSize: 24 },

  xpWrap: {
    display:     'flex',
    alignItems:  'center',
    gap:         12,
    padding:     '18px 24px 8px',
  },
  xpWrapMobile: {
    padding: '14px 16px 6px',
  },
  xpTrack: {
    flex:        1,
    height:      8,
    borderRadius:999,
    background:  '#f1f5f9',
    overflow:    'hidden',
    boxShadow:   'inset 0 1px 2px rgba(15, 23, 42, 0.05)',
  },
  xpFill: {
    height:      '100%',
    borderRadius:999,
    background:  'linear-gradient(90deg, #fbbf24, #f59e0b)',
    transition:  'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
    boxShadow:   '0 1px 3px rgba(251,191,36,0.4)',
  },
  xpLabel: {
    fontSize:   12,
    fontWeight: 700,
    color:      '#0f172a',
    flexShrink: 0,
    letterSpacing: '0.01em',
  },

  section: { padding: '8px 24px 0' },
  sectionTitle: {
    fontSize:   11.5,
    fontWeight: 700,
    color:      '#94a3b8',
    margin:     '14px 0 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  emptyWrap: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    padding:       '64px 32px 40px',
    gap:           18,
    textAlign:     'center',
  },
  emptyEmoji: {
    fontSize:  56,
    animation: 'orboot-bob 2s ease-in-out infinite',
    opacity:   0.85,
  },
  emptyText: {
    fontSize:   15,
    fontWeight: 500,
    color:      '#64748b',
    maxWidth:   280,
    margin:     0,
    lineHeight: 1.55,
  },
};

const qs = {
  card: {
    display:      'flex',
    alignItems:   'center',
    gap:          14,
    background:   '#ffffff',
    borderRadius: 14,
    padding:      '14px 16px',
    marginBottom: 8,
    boxShadow:    '0 1px 2px rgba(15, 23, 42, 0.04)',
    border:       '1px solid rgba(15, 23, 42, 0.06)',
    transition:   'transform 200ms var(--ease-out), box-shadow 200ms var(--ease-out)',
  },
  cardMobile: {
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
  },
  cardDone: {
    background:   '#f0fdf4',
    border:       '1px solid rgba(34, 197, 94, 0.20)',
  },
  icon:  { fontSize: 24, flexShrink: 0, opacity: 0.9 },
  body:  { flex: 1, minWidth: 0 },
  title: {
    fontSize:   14.5,
    fontWeight: 600,
    color:      '#0f172a',
    margin:     0,
    lineHeight: 1.25,
    letterSpacing: '-0.005em',
  },
  titleDone: { color: '#15803d' },
  desc: {
    fontSize:   12.5,
    color:      '#64748b',
    margin:     '3px 0 0',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  right:  { flexShrink: 0 },
  check:  { fontSize: 18 },
  checkWrap: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: '#22c55e',
    color: '#ffffff',
    display: 'grid',
    placeItems: 'center',
    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.32)',
  },
  xp: {
    background:   'rgba(251, 191, 36, 0.12)',
    color:        '#854d0e',
    border:       '1px solid rgba(251, 191, 36, 0.30)',
    borderRadius: 999,
    padding:      '4px 10px',
    fontSize:     11.5,
    fontWeight:   700,
    letterSpacing: '0.01em',
  },
};
